import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdAdd, MdClose } from "react-icons/md";
import axios from "../../../config/axiosInstance";
import { CRM } from "../../../config/apiConfig";
import { Badge } from "../Primitives";

/*
  Etiquetas de uma conta: mostra as que ela tem, tira com o X e coloca escolhendo da
  lista ou escrevendo um nome novo.

  `tags` vem da linha da conta, e não de uma consulta própria: depois de mudar, basta
  invalidar a conta para a tela inteira (cabeçalho, tabela) ver a mesma coisa.
*/

export const useTagOptions = () =>
  useQuery(
    ["crm", "tags"],
    async () => {
      const { data } = await axios.get(CRM.TAGS);
      return data.data;
    },
    { staleTime: 60 * 1000 }
  );

const TagPicker = ({ companyId, tags = [], onError }) => {
  const queryClient = useQueryClient();
  const { data: options = [] } = useTagOptions();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  const refresh = () => {
    queryClient.invalidateQueries(["crm", "client", String(companyId)]);
    queryClient.invalidateQueries(["crm", "tags"]);
    queryClient.invalidateQueries("companies");
  };

  const fail = (error) =>
    onError?.(
      error.response?.data?.message || "Não foi possível mudar a etiqueta."
    );

  const add = useMutation(
    async ({ tagId, newName }) => {
      const id =
        tagId ||
        (await axios.post(CRM.TAGS, { name: newName })).data.data.id;

      return axios.post(`${CRM.CLIENT_TAGS}/${companyId}/tags`, { tagId: id });
    },
    {
      onSuccess: () => {
        setName("");
        setAdding(false);
        refresh();
      },
      onError: fail,
    }
  );

  const remove = useMutation(
    (tagId) => axios.delete(`${CRM.CLIENT_TAGS}/${companyId}/tags/${tagId}`),
    { onSuccess: refresh, onError: fail }
  );

  const ownIds = new Set(tags.map((tag) => tag.id));
  const available = options.filter((option) => !ownIds.has(option.id));
  const typed = name.trim();
  const exact = options.find(
    (option) => option.name.toLowerCase() === typed.toLowerCase()
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <Badge key={tag.id} tone={tag.tone}>
          {tag.name}
          <button
            type="button"
            title={`Tirar a etiqueta ${tag.name}`}
            aria-label={`Tirar a etiqueta ${tag.name}`}
            disabled={remove.isLoading}
            onClick={() => remove.mutate(tag.id)}
            className="-mr-1 rounded-full opacity-60 transition hover:opacity-100"
          >
            <MdClose size={12} />
          </button>
        </Badge>
      ))}

      {adding ? (
        <form
          className="flex flex-wrap items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            if (!typed) return;
            add.mutate(exact ? { tagId: exact.id } : { newName: typed });
          }}
        >
          <input
            autoFocus
            list={`tags-${companyId}`}
            value={name}
            maxLength={40}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nome da etiqueta"
            className="w-40 rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-pink-300"
          />
          <datalist id={`tags-${companyId}`}>
            {available.map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
          <button
            type="submit"
            disabled={!typed || add.isLoading}
            className="rounded-lg bg-pink-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
          >
            {exact ? "Colocar" : "Criar"}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setName("");
            }}
            className="px-1 text-xs text-slate-500"
          >
            Cancelar
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-500 transition hover:border-pink-300 hover:text-pink-700"
        >
          <MdAdd size={13} /> etiqueta
        </button>
      )}
    </div>
  );
};

export default TagPicker;
